// AdminUsage — Block 6 (P1). Consultations volume + top candidates + party/cargo distribution.
import React, { useEffect, useState } from 'react';
import { adminAxios, useAdminAuth } from './AdminAuthContext';
import { Loader2, Activity, Search as SearchIcon, BarChart3, Clock } from 'lucide-react';

const Kpi = ({ icon: Icon, color, label, value, testid }) => (
  <div className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <div className="w-9 h-9 rounded-md flex items-center justify-center"
           style={{ background: color + '15', color }}>
        <Icon className="w-4 h-4" />
      </div>
    </div>
    <p className="text-[11px] uppercase tracking-wider text-[#475569]">{label}</p>
    <p className="text-2xl font-black text-[#0F1C2E]">{value}</p>
  </div>
);

const HBar = ({ rows, color, labelKey, valueKey, max }) => {
  if (!rows || rows.length === 0) return <p className="text-xs text-[#94A3B8]">Sem dados ainda.</p>;
  const m = max || Math.max(1, ...rows.map((r) => r[valueKey] || 0));
  return (
    <ul className="space-y-2">
      {rows.map((r, i) => (
        <li key={i}>
          <div className="flex justify-between text-[11px] mb-1">
            <span className="text-[#475569] truncate max-w-[60%]">{r[labelKey] || '—'}</span>
            <span className="font-bold text-[#0F1C2E]">{r[valueKey] || 0}</span>
          </div>
          <div className="w-full h-1.5 bg-[#F4F6F8] rounded">
            <div className="h-1.5 rounded"
                 style={{ width: `${((r[valueKey] || 0) / m) * 100}%`, background: color }} />
          </div>
        </li>
      ))}
    </ul>
  );
};

const AdminUsage = () => {
  const { token } = useAdminAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const r = await adminAxios(token).get('/api/admin/usage/overview');
        setData(r.data);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Falha ao carregar uso');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#1E3A5F]" /></div>;
  if (err) return <p className="text-sm text-[#DC2626]">{err}</p>;

  return (
    <div className="space-y-6" data-testid="admin-usage-page">
      <div>
        <h1 className="text-2xl font-black text-[#0F1C2E]">Uso e consultas</h1>
        <p className="text-xs text-[#475569]">Volume de análises e candidatos mais consultados</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi icon={Activity} color="#006030" label="Consultas hoje"
             value={data?.consultas_hoje ?? 0} testid="kpi-usage-today" />
        <Kpi icon={Activity} color="#1E3A5F" label="Últimos 7 dias"
             value={data?.consultas_7d ?? 0} testid="kpi-usage-7d" />
        <Kpi icon={Activity} color="#F59E0B" label="Últimos 30 dias"
             value={data?.consultas_30d ?? 0} testid="kpi-usage-30d" />
        <Kpi icon={Clock} color="#94A3B8"
             label="IA — tempo médio"
             value={data?.ia_tempo_medio ? `${data.ia_tempo_medio} ms` : '—'}
             testid="kpi-usage-ia" />
      </div>

      {data?.ia_tempo_medio == null && (
        <p className="text-[11px] text-[#94A3B8]">{data?.ia_tempo_medio_note}</p>
      )}

      {/* Top candidates */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid="usage-top-candidatos">
        <div className="flex items-center gap-2 mb-3">
          <SearchIcon className="w-4 h-4 text-[#1E3A5F]" />
          <h3 className="text-sm font-bold text-[#0F1C2E]">Top 20 candidatos consultados</h3>
        </div>
        {(data?.top_candidatos || []).length === 0 ? (
          <p className="text-xs text-[#94A3B8]">
            Sem consultas registradas ainda. Os candidatos mais consultados aparecerão aqui.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F8FAFC] text-[10px] uppercase tracking-wider text-[#475569] border-b border-[#E2E8F0]">
                  <th className="px-3 py-2 text-left">#</th>
                  <th className="px-3 py-2 text-left">Candidato</th>
                  <th className="px-3 py-2 text-left">Partido</th>
                  <th className="px-3 py-2 text-left">Cargo</th>
                  <th className="px-3 py-2 text-left">UF</th>
                  <th className="px-3 py-2 text-right">Consultas</th>
                </tr>
              </thead>
              <tbody>
                {data.top_candidatos.map((c, i) => (
                  <tr key={c.id || i} className="border-b border-[#F4F6F8] hover:bg-[#F8FAFC]">
                    <td className="px-3 py-2 text-[#475569] text-xs">{i + 1}</td>
                    <td className="px-3 py-2 text-[#0F1C2E] text-xs font-medium">{c.nome}</td>
                    <td className="px-3 py-2 text-[#475569] text-xs">{c.partido || '—'}</td>
                    <td className="px-3 py-2 text-[#475569] text-xs">{c.cargo || '—'}</td>
                    <td className="px-3 py-2 text-[#475569] text-xs">{c.estado || '—'}</td>
                    <td className="px-3 py-2 text-[#0F1C2E] text-xs font-bold text-right">{c.consultas}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Distributions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid="usage-by-party">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-4 h-4 text-[#1E3A5F]" />
            <h3 className="text-sm font-bold text-[#0F1C2E]">Por partido (Top 10)</h3>
          </div>
          <HBar rows={data?.por_partido || []} color="#1E3A5F"
                labelKey="partido" valueKey="count" />
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid="usage-by-cargo">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="w-4 h-4 text-[#006030]" />
            <h3 className="text-sm font-bold text-[#0F1C2E]">Por cargo</h3>
          </div>
          <HBar rows={data?.por_cargo || []} color="#006030"
                labelKey="cargo" valueKey="count" />
        </div>
      </div>
    </div>
  );
};

export default AdminUsage;
